import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Radio } from '@components/core-ui/form-elements/Radio';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { jobStatusCanBeChanged } from '@helpers/jobStatus/jobStatusCanBeChanged';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GetOrgMembersMemberResponseDto, JobDtoUser, JobResponseDto, JobsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import React from 'react';
import { getDefaultAssignStatus, LEAVE_STATUS_AS_IT_IS } from './AssignJobModal/helpers/getDefaultAssignStatus';
import { Content } from './AssignJobModal/styles/Content';
import { StyledTextarea } from './AssignJobModal/styles/StyledTextarea';
import { useAssigneeCandidates } from './hooks/useAssigneeCandidates';
import TeamEmptyWarningModal from './components/TeamsEmptyWarningModal';
import TeamsFilter from './components/TeamsFilter';
import TeamTag from './components/TeamTag';

type TProps = {
  job: JobResponseDto;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AssignJobModal = ({ job, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<GetOrgMembersMemberResponseDto | JobDtoUser | undefined>(job.assignee);
  const [status, setStatus] = React.useState<string>(() => getDefaultAssignStatus(job.status, Boolean(job.assignee)));
  const [comment, setComment] = React.useState('');

  const [chosenTeamsFilter, setChosenTeamsFilter] = React.useState<string[]>([]);

  const canChangeStatus = jobStatusCanBeChanged(job.status);

  const workspace = (organization?.workspaces ?? []).find((ws) => ws.id === job.workspaceId);

  const userHasManageMembersPermissionForCurrentWorkspace = (organization?.userTeams ?? []).some(
    (userTeam) =>
      userTeam.manageMembers && (workspace?.Teams ?? []).some((workspaceTeam) => workspaceTeam.slug === userTeam.slug),
  );

  const {
    users,
    teams: teamsFilterValues,
    isLoadingData,
  } = useAssigneeCandidates({
    organizationId: organization?.id ?? '',
    organizationSlug: organization?.slug ?? '',
    workspaceId: workspace?.id ?? '',
    workspaceSlug: workspace?.slug ?? '',
    canManageMembers: userHasManageMembersPermissionForCurrentWorkspace,
    search,
    teamsSlugFilter: chosenTeamsFilter.join('|'),
  });

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(undefined);
      setStatus(getDefaultAssignStatus(job.status, false));
      return;
    }
    setAssignee(data);
    setStatus(getDefaultAssignStatus(job.status, true));
  };

  const renderStatusRadioList = () => {
    if (!assignee) {
      return (
        <div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_TRANSCRIBING')}
              checked={status === 'JOB_STATUS_READY_FOR_TRANSCRIBING'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_TRANSCRIBING}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_PROOFREADING')}
              checked={status === 'JOB_STATUS_READY_FOR_PROOFREADING'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_PROOFREADING}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio
              onChange={() => setStatus('JOB_STATUS_READY_FOR_FINAL')}
              checked={status === 'JOB_STATUS_READY_FOR_FINAL'}
            >
              {JOB_STATUSES_ENUM.JOB_STATUS_READY_FOR_FINAL}
            </Radio>
          </div>
          <div className="radioWrapper">
            <Radio onChange={() => setStatus('JOB_STATUS_COMPLETE')} checked={status === 'JOB_STATUS_COMPLETE'}>
              {JOB_STATUSES_ENUM.JOB_STATUS_COMPLETE}
            </Radio>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus('JOB_STATUS_TRANSCRIBING')} checked={status === 'JOB_STATUS_TRANSCRIBING'}>
            {JOB_STATUSES_ENUM.JOB_STATUS_TRANSCRIBING}
          </Radio>
        </div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus('JOB_STATUS_PROOFING')} checked={status === 'JOB_STATUS_PROOFING'}>
            {JOB_STATUSES_ENUM.JOB_STATUS_PROOFING}
          </Radio>
        </div>
        <div className="radioWrapper">
          <Radio onChange={() => setStatus(LEAVE_STATUS_AS_IT_IS)} checked={status === LEAVE_STATUS_AS_IT_IS}>
            Leave as it is
          </Radio>
        </div>
      </div>
    );
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const reassignMutation = useMutation({
    mutationFn: () => {
      const workspaceId = job.workspaceId ?? workspace?.id ?? '';
      let newStatus = status === LEAVE_STATUS_AS_IT_IS ? job.status : status;
      if (!canChangeStatus) {
        newStatus = job.status;
      }
      return call(() =>
        JobsService.reassignJob({
          wsid: workspaceId,
          jobId: job.id,
          requestBody: {
            assigneeId: assignee?.id,
            status: newStatus as keyof typeof JOB_STATUSES_ENUM,
            assigneeComment: comment,
          },
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.detail(job.id) });
      void queryClient.invalidateQueries({ queryKey: jobsKeys.lists() });
      onSuccess();
    },
    onError: notifyApiCallError,
  });

  return (
    <ModalWrapper width="550px">
      <ModalTitle>Edit assignee</ModalTitle>
      <Content>
        {!teamsFilterValues.length && !isLoadingData ? (
          <TeamEmptyWarningModal
            linkToWorkspacesPage={`/${organization?.slug}/workspaces/${workspace?.slug}/details`}
            workspaceTitle={String(workspace?.name)}
          />
        ) : null}

        <p className="assignedTo">{job.assigneeId ? 'Currently assigned to' : 'Currently not assigned'}</p>
        {renderAssignee()}
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />

        <TeamsFilter
          teamsFilterValues={teamsFilterValues}
          chosenTeamsFilter={chosenTeamsFilter}
          setChosenTeamsFilter={setChosenTeamsFilter}
          isOrganizationTeams={userHasManageMembersPermissionForCurrentWorkspace}
        />

        <TableWrapper className="customTable">
          {isLoadingData ? <AbsoluteSpinner overlay className="mt-4 h-full" /> : null}
          <Table>
            <Thead $white>
              <Tr>
                <Th style={{ width: '1%' }}>Name</Th>
                <Th>Email</Th>
                <Th style={{ width: '1%' }}>Teams</Th>
              </Tr>
            </Thead>
            {!isLoadingData ? (
              <Tbody>
                {users.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <p className="pr-2 cursor-pointer" onClick={() => changeAssignee(data)}>
                        {data?.name} {data?.lastname}
                      </p>
                    </Td>
                    <Td>
                      <p
                        className="max-w-full overflow-hidden text-ellipsis px-2 cursor-pointer"
                        onClick={() => changeAssignee(data)}
                      >
                        {data?.email}
                      </p>
                    </Td>
                    <Td>
                      <div className="flex">
                        {data?.teams?.map((team) => (
                          <TeamTag
                            key={team.id}
                            team={team}
                            active={!chosenTeamsFilter.length || chosenTeamsFilter.includes(team.slug)}
                            onClick={() => changeAssignee(data)}
                          />
                        ))}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            ) : null}
          </Table>
        </TableWrapper>
        {canChangeStatus ? (
          <>
            <p className="text pb-2">Set status</p>
            {renderStatusRadioList()}
          </>
        ) : null}

        <p className="assignee-comment">Add a comment for assignee</p>
        <StyledTextarea
          label="Type your message here"
          name="assigneeComment"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          value={comment}
        />

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={() => reassignMutation.mutate()} disabled={reassignMutation.isPending}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
